import { Prompt, toast } from '@pcrm/fe/ui-kit';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { subscriptionsQueryKey } from '../subscriptions/subscriptions.api';
import { DOCUMENTS_QUERY_KEY, type DocumentListItem, deleteDocument } from './documents.api';

/**
 * Confirmarea ștergerii unei **ciorne** ([#142](https://github.com/bplugaru/efx-pcrm/issues/142)).
 *
 * **`Prompt`, nu sertar**, spre deosebire de anulare: anularea cere un motiv, care are nevoie
 * de un câmp; ștergerea nu cere nimic, doar o clipă de gândit. Un sertar cu un singur buton ar
 * fi fost două clicuri pentru o întrebare de unul.
 *
 * **Un hook, nu un component cu `document` și `onClose`**, pentru că îl cer două ecrane —
 * tabul de pe fișa clientului și registrul — iar amândouă ar fi trebuit să țină aceeași
 * bucată de stare. Aici o țin o singură dată: ecranul primește `ask` pentru meniu și
 * `element` pentru randare.
 *
 * ⚠️ **Textul nu explică ce se vede** (fondatorul, 2026-09-18). A fost, o vreme, un paragraf
 * despre ce anume dispare — versiunile, linkul de semnare, faptul că nu se poate da înapoi.
 * Cade sub aceeași regulă ca restul: ce trebuie știut se scrie în cod și în wiki, unde se
 * caută, nu în sertarul pe care operatorul îl deschide de douăzeci de ori pe zi. Vezi
 * contradicția 29 din `docs/wiki/contradictions.md`.
 *
 * Titlul spune **pe ce se apasă** (`Ștergi ciorna <tip>?`); descrierea întreabă, atât.
 */
export function useDeleteDocumentPrompt(): {
  ask: (document: DocumentListItem) => void;
  element: ReactNode;
} {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<DocumentListItem | null>(null);

  const mutation = useMutation({
    mutationFn: (document: DocumentListItem) => deleteDocument(document.id),
    onSuccess: async (_result, document) => {
      toast.success('Ciorna a fost ștearsă');
      await Promise.all([
        // Toate listele de documente: aceeași ciornă se vede și pe fișa clientului, și în
        // istoricul lui.
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY }),
        // Abonamentele acoperite de ciornă nu se schimbă la ștergere — n-au fost activate,
        // pentru că actul n-a fost semnat — dar legăturile către ele dispar, iar ecranul lor
        // arată „acoperit de un act în lucru".
        ...(document.customerId === null
          ? []
          : [
              queryClient.invalidateQueries({
                queryKey: subscriptionsQueryKey(document.customerId),
              }),
            ]),
      ]);
      setTarget(null);
    },
    onError: (error: unknown) => {
      // Mesajul serverului spune și ce e de făcut: un act emis se anulează, nu se șterge, iar
      // o ciornă cu linkul viu se invalidează întâi.
      toast.error(error instanceof ApiError ? error.message : 'Ciorna nu a putut fi ștearsă.');
      setTarget(null);
    },
  });

  return {
    ask: setTarget,
    element: (
      <Prompt
        open={target !== null}
        onOpenChange={(open) => !open && setTarget(null)}
        variant="danger"
      >
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>{target ? `Ștergi ciorna ${target.documentTypeName}?` : ''}</Prompt.Title>
            <Prompt.Description>Ești sigur că vrei să ștergi?</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Renunță</Prompt.Cancel>
            <Prompt.Action
              onClick={() => {
                if (target) {
                  mutation.mutate(target);
                }
              }}
            >
              Șterge
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    ),
  };
}
